import React, { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import CloseIcon from '@mui/icons-material/Close'
import dayjs from 'dayjs'
import { useIdleScreen } from 'src/customHooks/customHooks'
import { IdlePopupaStyledDialog } from 'src/clinic/components/CheckIn/components/styledComponents'
import { getActivePeekIns } from '../utils/peekInManager'
import { presentInRoom } from 'src/clinic/components/CheckIn/services'

/**
 * Check-in/Peek-in Idle Popup Component
 * Shows when user is idle while checked in or peeked in
 * Can be manually triggered via lock icon
 */
export function CheckInIdlePopup({ 
  clinicLocationData,
  clinicBrandData,
  mentorProfileData,
  manualTrigger = false,
  onManualTrigger,
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // Check if user is checked in or peeked in
  const checkedInDetails = useMemo(() => {
    try {
      const item = localStorage.getItem('checkedInDetails')
      return item ? JSON.parse(item) : null
    } catch {
      return null
    }
  }, [])

  const activePeekIns = useMemo(() => getActivePeekIns(), [])
  
  // Find peek-in for current clinic
  const currentPeekIn = useMemo(() => {
    if (!clinicLocationData?.id) return null
    return activePeekIns.find((peekIn) => peekIn.clinicLocationId === clinicLocationData.id)
  }, [activePeekIns, clinicLocationData?.id])

  const isCheckedIn = !!checkedInDetails
  const isPeekedIn = !!currentPeekIn
  const hasAccess = isCheckedIn || isPeekedIn

  // Use idle screen hook - 3 minutes of inactivity
  const { isIdle, resetIdle } = useIdleScreen({ idleTime: 180000, manualReset: true })
  
  // Show popup if manually triggered or automatically idle
  const showPopup = manualTrigger || (isIdle && hasAccess)

  const currentRoomName = checkedInDetails?.roomName || currentPeekIn?.title || currentPeekIn?.name || 'Room'
  const clinicName = clinicLocationData?.name || clinicLocationData?.title || 'Clinic'
  const brandName = clinicBrandData?.name || 'Brand'

  function logAndResetIdle() {
    if (currentPeekIn) {
      presentInRoom(currentPeekIn)
    }
    resetIdle()
    if (onManualTrigger) {
      onManualTrigger(false)
    }
  }

  // Don't show if not checked in or peeked in (unless manually triggered)
  if (!hasAccess && !manualTrigger) {
    return null
  }

  return (
    <IdlePopupaStyledDialog
      open={showPopup}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={false}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? '24px 24px 0 0' : '24px',
          maxHeight: isMobile ? '95vh' : '90vh',
          margin: isMobile ? 0 : '32px',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #6779e3 0%, #7450aa 100%)',
          color: 'white',
          padding: { xs: '18px 16px', sm: '22px 24px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
          <LockIcon sx={{ fontSize: { xs: 24, sm: 28 }, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '16px', sm: '17px' },
                mb: 0.5,
                letterSpacing: '-0.01em',
                fontFamily: "'Inter', sans-serif",
              }}
            >
              Screen paused
            </Typography>
            <Typography
              variant="body2"
              sx={{
                opacity: 0.95,
                fontSize: { xs: '12px', sm: '13px' },
                fontWeight: 400,
                letterSpacing: '0.01em',
              }}
            >
              {currentRoomName}
              {clinicName && ` • ${clinicName}`}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={logAndResetIdle}
          sx={{
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: { xs: '24px 16px', sm: '28px 24px' },
          paddingBottom: { xs: '24px', sm: '28px' },
          textAlign: 'center',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 2.5 },
          }}
        >
          <Box
            sx={{
              width: { xs: 64, sm: 72 },
              height: { xs: 64, sm: 72 },
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(103, 121, 227, 0.1) 0%, rgba(116, 80, 170, 0.1) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LockIcon sx={{ fontSize: { xs: 32, sm: 36 }, color: '#6779e3' }} />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '16px', sm: '17px' },
              color: '#1a1a1a',
              mb: 0.5,
            }}
          >
            Are you there?
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#6b7280',
              fontSize: { xs: '14px', sm: '15px' },
              maxWidth: '400px',
            }}
          >
            {manualTrigger 
              ? 'Screen is locked. Tap Continue to unlock.'
              : 'You seem to have gone idle for a while.'}
            {checkedInDetails?.createdTime && (
              <>
                <br />
                You {isCheckedIn ? 'checked in' : 'peeked in'} at {dayjs(checkedInDetails.createdTime || currentPeekIn?.peekedInAt).local().format('h:mm A')}
              </>
            )}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: { xs: '16px', sm: '24px' },
          justifyContent: 'center',
          gap: 1.5,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Button
          variant="contained"
          onClick={logAndResetIdle}
          sx={{
            background: 'linear-gradient(135deg, #6779e3 0%, #7450aa 100%)',
            color: 'white',
            fontWeight: 600,
            padding: { xs: '10px 24px', sm: '12px 32px' },
            borderRadius: '12px',
            textTransform: 'none',
            fontSize: { xs: '14px', sm: '15px' },
            minWidth: { xs: '100%', sm: '140px' },
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6bd1 0%, #5a3d88 100%)',
              boxShadow: '0 4px 12px rgba(103, 121, 227, 0.3)',
            },
          }}
        >
          Continue
        </Button>
      </DialogActions>
    </IdlePopupaStyledDialog>
  )
}

